import { DateFormat } from '@equip.health/ui';
import {
  areDatesInSameYear,
  formattedDateFromISO,
  isDateTodayOrInPast,
} from './date.util';
import { UserStatus } from '~/lib/constants/user.constants';
import type { CareTeamMemberCardProps } from '../types/careTeam/careTeam';
import type { UseGetCareTeamProvider } from '~/lib/hooks/care-team/useGetCareTeam';
import type { Reassignment } from '~/lib/types/reassignment';

export const isAssignmentActive = (
  assignment: { startDate?: string | null; endDate?: string | null },
): boolean => !!assignment.startDate && !assignment.endDate;

export const getTextForProviderAssignmentCard = (
  startDate: string,
  endDate?: string,
) => {
  if (endDate) {
    if (areDatesInSameYear(startDate, endDate)) {
      return `${formattedDateFromISO(startDate, DateFormat.shortMonthDay)} - ${formattedDateFromISO(endDate, DateFormat.shortMonthDayYear)}`;
    }
    return `${formattedDateFromISO(startDate, DateFormat.shortMonthDayYear)} - ${formattedDateFromISO(endDate, DateFormat.shortMonthDayYear)}`;
  }
  return `Assigned ${formattedDateFromISO(startDate, DateFormat.shortMonthDayYear)}`;
};

type ProviderActivityFields = Pick<
  CareTeamMemberCardProps,
  'userStatus' | 'endDate' | 'isIncomingReassignment'
>;

/** Determines whether a provider should appear in the active care team list. */
export const isProviderActiveForCareTeam = (
  provider: ProviderActivityFields,
): boolean => {
  if (provider.userStatus === UserStatus.INACTIVE) return false;
  if (provider.isIncomingReassignment) return false;
  return !provider.endDate || !isDateTodayOrInPast(provider.endDate);
};

/**
 * Tier 0: Active (no reassignment flags)
 * Tier 1: Active/reassigning (outgoing — has isOutgoingReassignment)
 * Tier 2: Inactive/pending (incoming — has isIncomingReassignment)
 * Tier 3: Inactive (ended assignment, inactivated user, or no reassignment)
 */
const getSortTier = (provider: UseGetCareTeamProvider): number => {
  if (provider.userStatus === UserStatus.INACTIVE) return 3;
  if (provider.isIncomingReassignment) return 2;
  if (provider.isOutgoingReassignment) return 1;

  const isActive = !provider.endDate || !isDateTodayOrInPast(provider.endDate);
  return isActive ? 0 : 3;
};

export const sortProvidersByActiveAndLastName = (
  providers: UseGetCareTeamProvider[],
) => {
  return [...providers].sort((a, b) => {
    const tierA = getSortTier(a);
    const tierB = getSortTier(b);

    if (tierA !== tierB) return tierA - tierB;

    return a.lastName.localeCompare(b.lastName);
  });
};

export const partitionCareTeamMembers = (
  careTeamMembers: CareTeamMemberCardProps[],
  initialCareTeamMembers: CareTeamMemberCardProps[],
): {
  initialMembers: CareTeamMemberCardProps[];
  newMembers: CareTeamMemberCardProps[];
  endedOrVoidMembers: CareTeamMemberCardProps[];
} => {
  const initialMemberIds = new Set(
    initialCareTeamMembers.map((member) => member.id),
  );

  const initialMembers = careTeamMembers.filter(
    (member) =>
      initialMemberIds.has(member.id) &&
      !member?.isEndAssignment &&
      !member?.isVoidAssignment,
  );

  const newMembers = careTeamMembers
    .filter((member) => !initialMemberIds.has(member.id))
    .sort((a, b) => a.lastName.localeCompare(b.lastName));

  const endOrVoidMembers = careTeamMembers.filter(
    (member) => member?.isEndAssignment || member?.isVoidAssignment,
  );

  return {
    endedOrVoidMembers: endOrVoidMembers,
    initialMembers,
    newMembers,
  };
};

type ProviderDetailMap = Map<
  string,
  Pick<
    GetProviderByIdResponse,
    | 'firstName'
    | 'lastName'
    | 'pronouns'
    | 'credentials'
    | 'userStatus'
    | 'outOfOffice'
  > & { avatarSrc?: string; displayFirstName?: string; displayLastName?: string }
>;

/** Merges reassignment data into the enriched provider list. */
export const mergeReassignmentsWithProviders = (
  providers: UseGetCareTeamProvider[],
  reassignments: Reassignment[],
  providerDetailMap: ProviderDetailMap,
): UseGetCareTeamProvider[] => {
  const activeReassignments = reassignments.filter(
    (r) => !r.isDeleted && r.processedAt === null,
  );

  const merged = [...providers];

  for (const reassignment of activeReassignments) {
    const departingIdx = merged.findIndex(
      (p) =>
        p.id === reassignment.departingProvider.id &&
        !p.isIncomingReassignment &&
        p.role === reassignment.providerTypeLabel,
    );

    if (departingIdx !== -1) {
      const hasNewProviderAndDate =
        reassignment.newProvider !== null &&
        reassignment.reassignmentDate !== null;

      merged[departingIdx] = {
        ...merged[departingIdx],
        isOutgoingReassignment: true,
        reassignmentId: reassignment.id,
        reassignmentDate: hasNewProviderAndDate
          ? reassignment.reassignmentDate
          : undefined,
        reassignmentFailureReason: reassignment.failureReason,
      };
    }

    if (reassignment.newProvider !== null) {
      const detail = providerDetailMap.get(reassignment.newProvider.id);
      const incomingRow: UseGetCareTeamProvider = {
        avatarSrc: detail?.avatarSrc,
        credentials: detail?.credentials,
        displayFirstName: detail?.displayFirstName,
        displayLastName: detail?.displayLastName,
        firstName: detail?.firstName ?? '',
        id: reassignment.newProvider.id,
        isEndAssignment: false,
        isIncomingReassignment: true,
        isTemporary: false,
        isVoidAssignment: false,
        lastName: detail?.lastName ?? '',
        outOfOffice: detail?.outOfOffice,
        pronouns: detail?.pronouns,
        providerId: reassignment.newProvider.id,
        reassignmentDate: reassignment.reassignmentDate ?? undefined,
        reassignmentId: reassignment.id,
        role: reassignment.providerTypeLabel,
        userStatus: detail?.userStatus ?? '',
      };
      merged.push(incomingRow);
    }
  }

  return merged;
};
